import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Alert, Box, Button, Stack, Typography} from '@mui/material'
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined'
import {useTranslation} from 'react-i18next'
import {toast} from 'react-toastify'
import VendorPaymentMethodsApi from '../../Api/vendorPaymentMethods'
import PopUp from '../customTable/PopUp'
import DeletedMsg from '../customTable/DeleteMsg'
import GatewayMetricsCards, {MetricsPayload} from './GatewayMetricsCards'
import GatewayTransactionsTable, {GatewayTransactionRow} from './GatewayTransactionsTable'
import GatewayGrid from './GatewayGrid'
import GatewayModal from './GatewayModal'
import CashCollectionTab from './CashCollectionTab'
import {PAYMENT_COLORS} from './constants'
import {
  AvailableCatalogGateway,
  buildGatewayCards,
  FilterTab,
  GatewayCardModel,
  MetricsApiGateway,
  VendorPaymentMethod,
} from './types'

export default function PaymentMethodsPageView() {
  const {t} = useTranslation()
  const [rows, setRows] = useState<VendorPaymentMethod[]>([])
  const [availableGateways, setAvailableGateways] = useState<AvailableCatalogGateway[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [loadError, setLoadError] = useState('')
  const [metrics, setMetrics] = useState<MetricsPayload | null>(null)
  const [metricsLoading, setMetricsLoading] = useState(false)
  const [txRows, setTxRows] = useState<GatewayTransactionRow[]>([])
  const [txLoading, setTxLoading] = useState(false)
  const [txFilters, setTxFilters] = useState({
    gateway: 'all',
    environment: 'all',
    status: '',
    period: '7d',
  })
  const [filter, setFilter] = useState<FilterTab>('all')
  const [modalOpen, setModalOpen] = useState(false)
  const [modalMode, setModalMode] = useState<'add' | 'manage' | 'connect'>('manage')
  const [activeCard, setActiveCard] = useState<GatewayCardModel | null>(null)
  const [deleteCard, setDeleteCard] = useState<GatewayCardModel | null>(null)
  const [openDelete, setOpenDelete] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const [busyFactory, setBusyFactory] = useState<string | null>(null)
  const [fadingOut, setFadingOut] = useState<string | null>(null)
  const [mainTab, setMainTab] = useState<'digital' | 'cash'>('digital')

  const loadMethods = useCallback(() => {
    setLoadError('')
    VendorPaymentMethodsApi.list((res: any) => {
      if (res?.ErrorStatus) {
        setLoadError(t('payment.load_error') as string)
        setRows([])
        return
      }
      const payload = res?.data
      const list = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : []
      setRows(list)
    }, setIsLoading)
  }, [t])

  const loadAvailable = useCallback(async () => {
    const countryId = Number(localStorage.getItem('country_id') || 0)
    const gateways = await VendorPaymentMethodsApi.getAvailableForCountry(
      countryId,
      'customer_checkout'
    )
    setAvailableGateways(
      (gateways || []).filter((item: AvailableCatalogGateway) => item.is_enabled)
    )
  }, [])

  const loadMetrics = useCallback(() => {
    VendorPaymentMethodsApi.getMetrics(
      txFilters.period,
      (res: any) => {
        setMetrics(res?.data || null)
      },
      setMetricsLoading
    )
  }, [txFilters.period])

  const loadTransactions = useCallback(() => {
    VendorPaymentMethodsApi.getTransactions(
      {
        period: txFilters.period,
        gateway: txFilters.gateway,
        environment: txFilters.environment,
        status: txFilters.status || undefined,
      },
      (res: any) => {
        const payload = res?.data
        const list = Array.isArray(payload?.data)
          ? payload.data
          : Array.isArray(payload)
            ? payload
            : []
        setTxRows(list)
      },
      setTxLoading
    )
  }, [txFilters])

  const refreshAll = useCallback(() => {
    loadMethods()
    loadMetrics()
    loadTransactions()
  }, [loadMethods, loadMetrics, loadTransactions])

  useEffect(() => {
    loadMethods()
    loadAvailable()
  }, [loadMethods, loadAvailable])

  useEffect(() => {
    loadMetrics()
    loadTransactions()
    const timer = window.setInterval(() => {
      loadMetrics()
      loadTransactions()
    }, 45000)
    return () => window.clearInterval(timer)
  }, [loadMetrics, loadTransactions])

  const cards = useMemo(
    () =>
      buildGatewayCards(
        rows,
        availableGateways,
        (metrics?.gateways || []) as MetricsApiGateway[]
      ),
    [rows, availableGateways, metrics]
  )

  const filteredCards = useMemo(() => {
    if (filter === 'connected') return cards.filter((c) => c.is_connected)
    if (filter === 'ready') return cards.filter((c) => !c.is_connected)
    return cards
  }, [cards, filter])

  const visibleCards = filteredCards.filter((c) => c.factory_name !== fadingOut)
  const showEmpty = !isLoading && cards.length === 0
  const showFilter = cards.length > 1

  const openConnect = (card: GatewayCardModel) => {
    setModalMode('connect')
    setActiveCard(card)
    setModalOpen(true)
  }

  const openManage = (card: GatewayCardModel) => {
    setModalMode('manage')
    setActiveCard(card)
    setModalOpen(true)
  }

  const requestDelete = (card: GatewayCardModel) => {
    setDeleteCard(card)
    setOpenDelete(true)
  }

  const confirmDelete = () => {
    if (!deleteCard) return
    const ids = rows
      .filter((r) => r.factory_name === deleteCard.factory_name)
      .map((r) => r.id)

    if (ids.length === 0) {
      setOpenDelete(false)
      setDeleteCard(null)
      return
    }

    setBusyFactory(deleteCard.factory_name)
    setIsDeleting(true)

    const removeNext = (index: number) => {
      if (index >= ids.length) {
        const nameKey = `payment.gateway.${
          deleteCard.factory_name === 'PY_WHISH'
            ? 'whish'
            : deleteCard.factory_name === 'PY_BLOM_UC'
              ? 'blom'
              : 'omt'
        }.name`
        setFadingOut(deleteCard.factory_name)
        window.setTimeout(() => {
          setFadingOut(null)
          setOpenDelete(false)
          setDeleteCard(null)
          setIsDeleting(false)
          setBusyFactory(null)
          toast.success(
            t('payment.success.deleted', {gateway: t(nameKey) as string}) as string
          )
          refreshAll()
        }, 280)
        return
      }

      VendorPaymentMethodsApi.remove(
        ids[index],
        (data: any) => {
          if (data?.ErrorStatus || data?.msgErr) {
            toast.error(
              `${t('payment.errors.delete_failed') as string}: ${
                data?.msgErr || data?.message || ''
              }`
            )
            setIsDeleting(false)
            setBusyFactory(null)
            return
          }
          removeNext(index + 1)
        },
        () => undefined
      )
    }

    removeNext(0)
  }

  const filterButtons: {id: FilterTab; labelKey: string}[] = [
    {id: 'all', labelKey: 'payment.filter_all'},
    {id: 'connected', labelKey: 'payment.filter_connected'},
    {id: 'ready', labelKey: 'payment.filter_ready'},
  ]

  return (
    <Stack gap={2}>
      <Stack direction='row' flexWrap='wrap' gap={1}>
        {(
          [
            {id: 'digital' as const, labelKey: 'payment.tab_digital'},
            {id: 'cash' as const, labelKey: 'payment.tab_cash'},
          ] as const
        ).map((tab) => {
          const active = mainTab === tab.id
          return (
            <Button
              key={tab.id}
              onClick={() => setMainTab(tab.id)}
              sx={{
                height: 40,
                px: 2.5,
                fontWeight: 600,
                fontSize: 14,
                textTransform: 'none',
                borderRadius: '8px',
                backgroundColor: active ? PAYMENT_COLORS.primary : PAYMENT_COLORS.surface,
                color: active ? '#fff' : PAYMENT_COLORS.textSecondary,
                border: `1px solid ${active ? PAYMENT_COLORS.primary : PAYMENT_COLORS.border}`,
                '&:hover': {
                  backgroundColor: active ? '#244A96' : PAYMENT_COLORS.background,
                },
              }}
            >
              {t(tab.labelKey) as string}
            </Button>
          )
        })}
      </Stack>

      {mainTab === 'cash' ? (
        <Box
          sx={{
            p: {xs: '16px', sm: '24px', lg: '32px'},
            backgroundColor: PAYMENT_COLORS.background,
            borderRadius: '12px',
            border: `1px solid ${PAYMENT_COLORS.border}`,
          }}
        >
          <CashCollectionTab />
        </Box>
      ) : (
        <>
      <Box
        sx={{
          p: {xs: '16px', sm: '24px', lg: '32px'},
          backgroundColor: PAYMENT_COLORS.background,
          borderRadius: '12px',
          border: `1px solid ${PAYMENT_COLORS.border}`,
        }}
      >
        <Box mb={2}>
          <Typography
            fontSize={28}
            fontWeight={600}
            color={PAYMENT_COLORS.textPrimary}
            lineHeight={1.2}
          >
            {t('payment.page_title') as string}
          </Typography>
          <Typography fontSize={14} color={PAYMENT_COLORS.textSecondary} mt={0.5}>
            {t('payment.page_subtitle') as string}
          </Typography>
        </Box>

        {loadError ? (
          <Stack gap={2} mb={3}>
            <Alert severity='warning'>{loadError}</Alert>
            <Button variant='outlined' onClick={loadMethods} sx={{alignSelf: 'flex-start'}}>
              {t('payment.retry') as string}
            </Button>
          </Stack>
        ) : null}

        {/* Add payment method is admin-only — no vendor Add button */}

        {showFilter ? (
          <Stack direction='row' flexWrap='wrap' gap={1} mb={2}>
            {filterButtons.map((btn) => {
              const active = filter === btn.id
              return (
                <Button
                  key={btn.id}
                  onClick={() => setFilter(btn.id)}
                  sx={{
                    height: 36,
                    px: 2,
                    fontWeight: 600,
                    fontSize: 13,
                    textTransform: 'none',
                    borderRadius: '6px',
                    backgroundColor: active ? PAYMENT_COLORS.primary : PAYMENT_COLORS.surface,
                    color: active ? '#fff' : PAYMENT_COLORS.textSecondary,
                    border: `1px solid ${
                      active ? PAYMENT_COLORS.primary : PAYMENT_COLORS.border
                    }`,
                    '&:hover': {
                      backgroundColor: active ? '#244A96' : PAYMENT_COLORS.background,
                    },
                  }}
                >
                  {t(btn.labelKey) as string}
                </Button>
              )
            })}
          </Stack>
        ) : null}

        {showEmpty ? (
          <Stack
            alignItems='center'
            justifyContent='center'
            sx={{mt: '80px', mb: 6, textAlign: 'center'}}
            gap={1.5}
          >
            <AccountBalanceWalletOutlinedIcon
              sx={{fontSize: 56, color: PAYMENT_COLORS.textMuted}}
            />
            <Typography fontSize={20} fontWeight={600} color={PAYMENT_COLORS.textPrimary}>
              {t('payment.empty_title') as string}
            </Typography>
            <Typography fontSize={14} color={PAYMENT_COLORS.textSecondary} maxWidth={360}>
              {t('payment.empty_subtitle') as string}
            </Typography>
          </Stack>
        ) : (
          <Box
            sx={{
              mt: showFilter ? 1 : 0,
              '& > *': {
                transition: 'opacity 280ms ease',
              },
            }}
          >
            <GatewayGrid
              cards={visibleCards}
              isLoading={isLoading}
              metricsLoading={metricsLoading}
              busyFactory={busyFactory}
              onConnect={openConnect}
              onManage={openManage}
              onDelete={requestDelete}
            />
          </Box>
        )}
      </Box>

      <div className={`card fds sha`}>
        <Box p={{xs: '15px', md: '24px'}}>
          <Typography fontWeight={700} fontSize={16} mb={2} color='var(--bs-text-dark)'>
            {t('payment.section_metrics') as string}
          </Typography>
          <GatewayMetricsCards metrics={metrics} isLoading={metricsLoading} />
        </Box>
      </div>

      <div className={`card fds sha`}>
        <Box p={{xs: '15px', md: '24px'}}>
          <Typography fontWeight={700} fontSize={16} mb={2} color='var(--bs-text-dark)'>
            {t('payment.section_transactions') as string}
          </Typography>
          <GatewayTransactionsTable
            rows={txRows}
            filters={txFilters}
            onFiltersChange={setTxFilters}
            isLoading={txLoading}
          />
        </Box>
      </div>

      <PopUp open={modalOpen} setOpen={setModalOpen} width='sm' isLoading={false}>
        <GatewayModal
          open={modalOpen}
          mode={modalMode}
          card={activeCard}
          availableGateways={availableGateways}
          configured={rows}
          onSuccess={() => {
            setModalOpen(false)
            setActiveCard(null)
            refreshAll()
          }}
          onClose={() => {
            setModalOpen(false)
            setActiveCard(null)
          }}
        />
      </PopUp>

      <DeletedMsg
        open={openDelete}
        setOpen={setOpenDelete}
        onClick={confirmDelete}
        isLoadingDelete={isDeleting}
        deleteMsg='payment.confirm.delete_message'
      />
        </>
      )}
    </Stack>
  )
}
